/**
 * Deal EBITDA Adjustments Panel (SPRINT-25 F006)
 *
 * Inline CRUD panel for managing deal-level EBITDA adjustments within the
 * Deal Financial Hub Consolidated tab.
 *
 * Placed between IntercompanyEliminationsPanel and CalculationsSummary.
 *
 * Adjustment types: owner_salary, related_rent, non_recurring_income,
 * non_recurring_expense, related_party_transaction, personal_expense,
 * covid_impact, custom.
 *
 * Direction: add (↑ green) or remove (↓ red).
 * Custom type requires customLabel field.
 * Summary row shows net total of all adjustments.
 */

import React, { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, Pencil, Trash2, Loader2, Info } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type AdjustmentType =
  | "owner_salary"
  | "related_rent"
  | "non_recurring_income"
  | "non_recurring_expense"
  | "related_party_transaction"
  | "personal_expense"
  | "covid_impact"
  | "custom";

type AdjustmentDirection = "add" | "remove";

interface DealEbitdaAdjustment {
  id: string;
  dealId: string;
  periodLabel: string;
  adjustmentType: AdjustmentType;
  customLabel: string | null;
  description: string | null;
  amount: string;
  direction: AdjustmentDirection;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface FormState {
  adjustmentType: AdjustmentType;
  customLabel: string;
  description: string;
  periodLabel: string;
  amount: string;
  direction: AdjustmentDirection;
  notes: string;
}

export interface DealEbitdaAdjustmentsPanelProps {
  dealId: string;
  /** Available period labels from deal analysis (e.g. ["FY2024", "FY2023"]) */
  periods?: string[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ADJUSTMENT_TYPE_LABELS: Record<AdjustmentType, string> = {
  owner_salary: "Owner Salary",
  related_rent: "Related Rent",
  non_recurring_income: "Non-Recurring Income",
  non_recurring_expense: "Non-Recurring Expense",
  related_party_transaction: "Related Party Transaction",
  personal_expense: "Personal Expense",
  covid_impact: "COVID Impact",
  custom: "Custom",
};

const ADJUSTMENT_TYPE_OPTIONS = (
  Object.entries(ADJUSTMENT_TYPE_LABELS) as [AdjustmentType, string][]
).map(([value, label]) => ({ value, label }));

const DEFAULT_FORM: FormState = {
  adjustmentType: "owner_salary",
  customLabel: "",
  description: "",
  periodLabel: "",
  amount: "",
  direction: "add",
  notes: "",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(value));
}

function formatAmount(amount: string): string {
  const n = Number(amount);
  if (isNaN(n)) return amount;
  return formatCurrency(n);
}

function parseAmountInput(raw: string): string {
  // Strip currency symbols and commas for storage
  return raw.replace(/[^0-9.]/g, "");
}

function getTypeLabel(adj: DealEbitdaAdjustment): string {
  if (adj.adjustmentType === "custom" && adj.customLabel) {
    return `Custom: ${adj.customLabel}`;
  }
  return ADJUSTMENT_TYPE_LABELS[adj.adjustmentType] ?? adj.adjustmentType;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DealEbitdaAdjustmentsPanel: React.FC<
  DealEbitdaAdjustmentsPanelProps
> = ({ dealId, periods = [] }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [modalMode, setModalMode] = useState<"add" | "edit" | null>(null);
  const [editingAdj, setEditingAdj] = useState<DealEbitdaAdjustment | null>(
    null,
  );
  const [deleteAdj, setDeleteAdj] =
    useState<DealEbitdaAdjustment | null>(null);
  const [formState, setFormState] = useState<FormState>(DEFAULT_FORM);
  const [customLabelError, setCustomLabelError] = useState(false);

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const { data: adjustments = [], isLoading } = useQuery<
    DealEbitdaAdjustment[]
  >({
    queryKey: [`/api/deal-analysis/deals/${dealId}/ebitda-adjustments`],
    enabled: !!dealId,
  });

  // -------------------------------------------------------------------------
  // Computed totals
  // -------------------------------------------------------------------------

  const netTotal = adjustments.reduce((sum, adj) => {
    const n = Number(adj.amount);
    return adj.direction === "add" ? sum + n : sum - n;
  }, 0);

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiRequest(
        "POST",
        `/api/deal-analysis/deals/${dealId}/ebitda-adjustments`,
        data,
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/deal-analysis/deals"],
      });
      closeModal();
      toast({ title: "Adjustment saved", description: "Recalculate consolidated analysis to see updated EBITDA" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save adjustment",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      const res = await apiRequest(
        "PATCH",
        `/api/deal-analysis/ebitda-adjustments/${id}`,
        data,
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/deal-analysis/deals"],
      });
      closeModal();
      toast({ title: "Adjustment saved", description: "Recalculate consolidated analysis to see updated EBITDA" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update adjustment",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/deal-analysis/ebitda-adjustments/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/deal-analysis/deals"],
      });
      setDeleteAdj(null);
      toast({ title: "Adjustment deleted" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete adjustment",
        variant: "destructive",
      });
    },
  });

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const openAdd = useCallback(() => {
    setEditingAdj(null);
    setCustomLabelError(false);
    setFormState({
      ...DEFAULT_FORM,
      periodLabel: periods[0] ?? "",
    });
    setModalMode("add");
  }, [periods]);

  const openEdit = useCallback((adj: DealEbitdaAdjustment) => {
    setEditingAdj(adj);
    setCustomLabelError(false);
    setFormState({
      adjustmentType: adj.adjustmentType,
      customLabel: adj.customLabel ?? "",
      description: adj.description ?? "",
      periodLabel: adj.periodLabel,
      amount: String(Number(adj.amount)),
      direction: adj.direction,
      notes: adj.notes ?? "",
    });
    setModalMode("edit");
  }, []);

  const closeModal = useCallback(() => {
    setModalMode(null);
    setEditingAdj(null);
    setCustomLabelError(false);
    setFormState(DEFAULT_FORM);
  }, []);

  const updateField = useCallback(
    <K extends keyof FormState>(field: K, value: FormState[K]) => {
      setFormState((prev) => ({ ...prev, [field]: value }));
      if (field === "customLabel" && value) {
        setCustomLabelError(false);
      }
    },
    [],
  );

  const handleSave = useCallback(async () => {
    // Validate custom label
    if (formState.adjustmentType === "custom" && !formState.customLabel.trim()) {
      setCustomLabelError(true);
      return;
    }

    const amount = parseAmountInput(formState.amount);
    const n = parseFloat(amount);
    if (isNaN(n) || n <= 0) return;

    const payload: Record<string, unknown> = {
      adjustmentType: formState.adjustmentType,
      customLabel:
        formState.adjustmentType === "custom"
          ? formState.customLabel.trim()
          : null,
      description: formState.description || null,
      periodLabel: formState.periodLabel,
      amount,
      direction: formState.direction,
      notes: formState.notes || null,
    };

    if (modalMode === "edit" && editingAdj) {
      await updateMutation.mutateAsync({ id: editingAdj.id, data: payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
  }, [formState, modalMode, editingAdj, createMutation, updateMutation]);

  const handleAmountBlur = useCallback(() => {
    const raw = parseAmountInput(formState.amount);
    const n = parseFloat(raw);
    if (!isNaN(n) && n > 0) {
      setFormState((prev) => ({ ...prev, amount: String(n) }));
    }
  }, [formState.amount]);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <>
      <Card
        className="border-0 shadow-none"
        data-testid="panel-deal-ebitda-adjustments"
      >
        <CardHeader className="px-0 py-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-medium">
            Deal-Level EBITDA Adjustments
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={openAdd}
            data-testid="button-add-ebitda-adjustment"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Adjustment
          </Button>
        </CardHeader>

        <CardContent className="px-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2
                className="h-6 w-6 animate-spin"
                data-testid="ebitda-adj-loading"
              />
            </div>
          ) : adjustments.length === 0 ? (
            <div
              className="space-y-3"
              data-testid="ebitda-adj-empty-state"
            >
              <p className="text-sm text-muted-foreground text-center py-4">
                No deal-level adjustments
              </p>
              <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
                <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  Deal-level adjustments apply to the consolidated analysis.
                  For entity-specific normalisation, use the entity financial
                  view.
                </span>
              </div>
            </div>
          ) : (
            <div data-testid="ebitda-adj-table-container">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Direction</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {adjustments.map((adj) => (
                    <TableRow
                      key={adj.id}
                      data-testid={`row-ebitda-adj-${adj.id}`}
                    >
                      <TableCell className="py-2">
                        <Badge variant="secondary" className="font-normal">
                          {getTypeLabel(adj)}
                        </Badge>
                      </TableCell>
                      <TableCell
                        className="py-2 text-sm text-muted-foreground"
                        data-testid={`adj-description-${adj.id}`}
                      >
                        {adj.description || "\u2014"}
                      </TableCell>
                      <TableCell
                        className="py-2 text-sm"
                        data-testid={`adj-period-${adj.id}`}
                      >
                        {adj.periodLabel}
                      </TableCell>
                      <TableCell className="py-2">
                        {adj.direction === "add" ? (
                          <span
                            className="flex items-center gap-1 text-green-600 text-sm font-medium"
                            data-testid={`adj-direction-${adj.id}`}
                          >
                            ↑ Add
                          </span>
                        ) : (
                          <span
                            className="flex items-center gap-1 text-red-600 text-sm font-medium"
                            data-testid={`adj-direction-${adj.id}`}
                          >
                            ↓ Remove
                          </span>
                        )}
                      </TableCell>
                      <TableCell
                        className="py-2 text-right text-sm font-mono"
                        data-testid={`adj-amount-${adj.id}`}
                      >
                        {formatAmount(adj.amount)}
                      </TableCell>
                      <TableCell className="py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => openEdit(adj)}
                            aria-label="edit adjustment"
                            data-testid={`button-edit-${adj.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                            onClick={() => setDeleteAdj(adj)}
                            aria-label="delete adjustment"
                            data-testid={`button-delete-${adj.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Summary row */}
              <div
                className="flex justify-between items-center border-t pt-3 mt-1 px-1"
                data-testid="ebitda-adj-total-row"
              >
                <span className="text-sm font-semibold text-muted-foreground">
                  Total Adjustments
                </span>
                <span
                  className={`text-sm font-bold font-mono ${netTotal >= 0 ? "text-green-600" : "text-red-600"}`}
                  data-testid="ebitda-adj-net-total"
                >
                  {netTotal >= 0
                    ? `+${formatCurrency(netTotal)}`
                    : `-${formatCurrency(Math.abs(netTotal))}`}
                </span>
              </div>

              <p className="text-xs text-muted-foreground mt-2 px-1">
                Note: Recalculate consolidated analysis to see updated EBITDA
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog
        open={modalMode !== null}
        onOpenChange={(open) => !open && closeModal()}
      >
        <DialogContent
          className="max-w-lg max-h-[90vh] overflow-y-auto"
          data-testid="ebitda-adj-modal"
        >
          <DialogHeader>
            <DialogTitle>
              {modalMode === "edit" ? "Edit Adjustment" : "Add Adjustment"}
            </DialogTitle>
            <DialogDescription>
              {modalMode === "edit"
                ? "Update the deal-level EBITDA adjustment."
                : "Add a deal-level EBITDA adjustment to the consolidated analysis."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Adjustment Type */}
            <div className="space-y-2">
              <Label htmlFor="adj-type">Adjustment Type</Label>
              <Select
                value={formState.adjustmentType}
                onValueChange={(val) =>
                  updateField("adjustmentType", val as AdjustmentType)
                }
              >
                <SelectTrigger
                  id="adj-type"
                  data-testid="select-adjustment-type"
                >
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {ADJUSTMENT_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Custom Label — shown only when type=custom */}
            {formState.adjustmentType === "custom" && (
              <div className="space-y-2">
                <Label htmlFor="adj-custom-label">
                  Custom Label{" "}
                  <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="adj-custom-label"
                  value={formState.customLabel}
                  onChange={(e) => updateField("customLabel", e.target.value)}
                  placeholder="e.g., One-off restructuring charge"
                  data-testid="input-custom-label"
                  className={customLabelError ? "border-destructive" : ""}
                />
                {customLabelError && (
                  <p
                    className="text-sm text-destructive"
                    data-testid="error-custom-label"
                  >
                    Custom label is required for custom adjustment type.
                  </p>
                )}
              </div>
            )}

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="adj-description">Description</Label>
              <Input
                id="adj-description"
                value={formState.description}
                onChange={(e) => updateField("description", e.target.value)}
                placeholder="e.g., Owner salary normalisation"
                data-testid="input-adj-description"
              />
            </div>

            {/* Period Label */}
            <div className="space-y-2">
              <Label htmlFor="adj-period">Period</Label>
              {periods.length > 0 ? (
                <Select
                  value={formState.periodLabel}
                  onValueChange={(val) => updateField("periodLabel", val)}
                >
                  <SelectTrigger
                    id="adj-period"
                    data-testid="select-period-label"
                  >
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    {periods.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="adj-period"
                  value={formState.periodLabel}
                  onChange={(e) => updateField("periodLabel", e.target.value)}
                  placeholder="e.g., FY2024"
                  data-testid="input-period-label"
                />
              )}
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="adj-amount">Amount ($)</Label>
              <Input
                id="adj-amount"
                type="number"
                step="1"
                min="0"
                value={formState.amount}
                onChange={(e) => updateField("amount", e.target.value)}
                onBlur={handleAmountBlur}
                placeholder="e.g., 55000"
                data-testid="input-adj-amount"
              />
            </div>

            {/* Direction */}
            <div className="space-y-2">
              <Label>Direction</Label>
              <RadioGroup
                value={formState.direction}
                onValueChange={(val) =>
                  updateField("direction", val as AdjustmentDirection)
                }
                className="flex gap-6"
                data-testid="radio-direction"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="add" id="dir-add" />
                  <Label
                    htmlFor="dir-add"
                    className="text-green-600 font-medium cursor-pointer"
                  >
                    ↑ Add
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="remove" id="dir-remove" />
                  <Label
                    htmlFor="dir-remove"
                    className="text-red-600 font-medium cursor-pointer"
                  >
                    ↓ Remove
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="adj-notes">Notes</Label>
              <textarea
                id="adj-notes"
                value={formState.notes}
                onChange={(e) => updateField("notes", e.target.value)}
                placeholder="Additional notes..."
                data-testid="input-adj-notes"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              data-testid="button-save-adjustment"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Adjustment"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteAdj}
        onOpenChange={(open) => !open && setDeleteAdj(null)}
      >
        <AlertDialogContent data-testid="ebitda-adj-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this adjustment?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove the{" "}
              {deleteAdj ? getTypeLabel(deleteAdj) : "adjustment"}? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteAdj && deleteMutation.mutate(deleteAdj.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DealEbitdaAdjustmentsPanel;
